import React, { useEffect, useState, useCallback } from 'react'
import toLower from 'lodash/fp/toLower'
import first from 'lodash/first'
import find from 'lodash/find'
import PropTypes from 'prop-types'
import { useIntl, defineMessages } from 'react-intl'
import { useParams } from 'react-router-dom'
import size from 'lodash/fp/size'
import get from 'lodash/get'
import map from 'lodash/map'
import every from 'lodash/every'
import xor from 'lodash/xor'
import { styled } from '@mui/material/styles'
import { Button, Theme, Typography } from '@mui/material'
import { ReactComponent as Alert } from '@mike/mike-shared-frontend/media/icons/Alert'
import MikeSlidingPanel from '../SlidingPanel'
import AppPropTypes from '../../shared/appPropTypes'
import messages from '../../shared/messages'
import { enhanceProjectMembers } from '../../helpers/project'
import RefreshButton from '../buttons/RefreshButton'
import DownloadButton from '../buttons/DownloadButton'
import DeleteButton from '../buttons/DeleteButton'
import MoveToButton from '../buttons/MoveToButton'
import CreateSubprojectButton from '../buttons/CreateSubprojectButton'
import FileChooserButton from '../buttons/FileChooserButton'
import FolderAccessButton from '../buttons/FolderAccessButton'
import SearchInput from '../SearchInput'
import ConfirmationDialog from '../Dialog/ConfirmationDialog'
import EditDatasetForm from '../EditDatasetForm'
import CreateProjectForm from '../CreateProjectForm'
import ConnectedProjectContentTable from '../../containers/ConnectedProjectContentTable'
import ProjectsTreeDialog from '../ProjectsTreeDialog'
import AppNav from '../AppNav'
import DatasetConversionDialog, {
  dialogMode
} from '../DatasetConversionDialog/DatasetConversionDialog'
import IDatasetReader from '../DatasetConversionDialog/model/IDatasetReader'
import DatasetUploadManyDialog from '../DatasetConversionDialog/DatasetUploadManyDialog'
import DatasetDownloadManyDialog from '../DatasetConversionDialog/DatasetDownloadManyDialog'
import { UPLOAD_TYPE } from '../../helpers/upload'
import { CONVERT_TYPE } from '../../helpers/convert'
import { DOWNLOAD_TYPE } from '../../helpers/export'
import { useTypedSelector, useAppDispatch } from '../../reducers'
import AppBreadcrumbNav from '../AppBreadcrumpsNav'
import FolderAccessDialog from '../FolderAccessDialog'

import { loadCustomerUsers } from '../../actions/customerUsers'
import {
  loadProjectDetails,
  loadProjectSubprojects,
  deleteProjectDatasets,
  updateProjectDataset,
  deleteSubProjects,
  updateProjectDetails,
  moveContent,
  loadProjectRoot,
  resetProjectInitialTree,
  setProjectPagination,
  loadProjectContent,
  setProjectRowsPerPage,
  loadProjectMembers
} from '../../actions/project'
import { loadDatasetReaders } from '../../actions/datasetReaders'
import { loadDatasetWriters } from '../../actions/datasetWriters'
import { createNewFolder } from '../../actions/projects'
import { addError } from '../../actions/errors'
import {
  uploadAndImportFile,
  uploadAndImportManyFiles
} from '../../actions/uploadAndImport'
import {
  exportAndDownloadDataset,
  exportAndDownloadManyDatasets
} from '../../actions/exportAndDownload'
import { convertDataset } from '../../actions/converts'
import {
  filterCoordinateSystems,
  getCoordinateSystemsById
} from '../../actions/coordinateSystems'
import { isNumeric } from '../../helpers/common'

import asIfReaderWriter, {
  FILEWRITER
} from '../DatasetConversionDialog/model/asIfReaderWriter'
import { isDataset, DATASETFORMAT } from '../../helpers/projectContent'
import MoveDatasetDialog from '../MoveDatasetDialog/MoveDatasetDialog'
import CopyDatasetDialog from '../CopyDatasetDialog/CopyDatasetDialog'

const localMessages = defineMessages({
  projectTableHeader: {
    id: 'projectContent.table.header'
  },
  confirmationDeleteTitle: {
    id: 'projectContent.confirmation.deleteItems.title'
  },
  searchPlaceholder: {
    id: 'projectDetails.input.search.placeholder'
  },
  editProjectButtonTipFolder: {
    id: 'projectDetails.button.editProject.tipFolder'
  },
  manageAccess: {
    id: 'projectContent.manageAccess',
    defaultMessage: 'Folder Access'
  },
  detailsFailed: {
    id: 'projectContent.detailsFailed',
    defaultMessage:
      'This folder could not be loaded, so its actions are unavailable.'
  },
  detailsFailedRetry: {
    id: 'projectContent.detailsFailed.retry',
    defaultMessage: 'Try again'
  },
  descriptionLabel: {
    id: 'projectContent.descriptionLabel'
  },
  membersLabel: {
    id: 'projectContent.membersLabel'
  },
  accessLevelLabel: {
    id: 'projectContent.accessLevelLabel'
  },
  noWriterToDownload: {
    id: 'projectContent.noWriterToDownload'
  },
  noWriterToConvert: {
    id: 'projectContent.noWriterToConvert'
  },
  editProjectTitle: {
    id: 'createProjectForm.heading.label'
  },
  createFolderTitle: {
    id: 'createProjectForm.headingFolder.title'
  },
  editDatasetTitle: {
    id: 'editDatasetForm.heading.label.dataset'
  },
  editFileTitle: {
    id: 'editDatasetForm.heading.label.file'
  }
})

type MikeTheme = Theme & {
  palette: Theme['palette'] & {
    lightGrey?: { main: string }
    mediumGrey?: { main: string }
  }
}

const StyledProjectDetails = styled('div')(({ theme }) => ({
  display: 'flex',
  paddingLeft: '30px',
  paddingRight: theme.spacing(2),
  paddingBottom: '16px'
}))

const StyledTableTopHeader = styled('div')(({ theme }) => {
  const t = theme as MikeTheme
  return {
    // Sets the search row's right edge; its left edge is set on the row.
    paddingRight: '30px',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    backgroundColor: t.palette.lightGrey?.main
  }
})

const StyledTableTopActionButtons = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  paddingBottom: theme.spacing(3),
  marginLeft: 0,
  paddingLeft: '30px'
}))

const StyledLeftActionButtons = styled('div')({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-end'
})

const StyledRightActionButtons = styled('div')({
  display: 'flex',
  justifyContent: 'center'
})

const StyledCreateSubprojectButton = styled('div')({
  marginLeft: 20
})

const StyledFolderAccessButton = styled('div')({
  marginLeft: 20
})

const StyledDetailsError = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginLeft: theme.spacing(5),
  marginRight: theme.spacing(4),
  paddingBottom: theme.spacing(2),
  color: theme.palette.error.main
}))

const ProjectContent = ({
  // customerId,eslint-disable-next-line react/prop-types
  customerUsers,
  loadingInitialTree,
  project,
  projectTree,
  user,
  datasetReaders,
  datasetFileReaders,
  showDatasetReadersLoader,
  datasetWriters,
  dedicatedWriters,
  showDatasetWritersLoader,
  loadingCoordinateSystems,
  selectedCoordinateSystems,
  searchCoordinateSystemsById,
  totalCount,
  page,
  rowsPerPage,
  namePrefix,
  sortBy,
  sortOrder
}) => {
  const intl = useIntl()
  const tenantId = useTypedSelector(state => state['auth'].user?.tenantId)
  const dispatch = useAppDispatch()
  const routeParams = useParams<{ id: string }>()
  const routeProjectId = routeParams.id

  const loadingDetails = useTypedSelector(
    state => state['project'].details.loading
  )
  const detailsProjectId = useTypedSelector(
    state => state['project'].details.projectId
  )

  const detailsFailed =
    !loadingDetails && !project && detailsProjectId === routeProjectId

  const onRetryDetails = useCallback(() => {
    dispatch(loadProjectDetails(routeProjectId, false))
  }, [dispatch, routeProjectId])

  // Migrated from recompose withState (in ConnectedProjectContent)
  const [searchText, setSearchText] = useState('')
  const [selectedItems, setSelectedItems] = useState<any[]>([])
  const [openCreateProject, setOpenCreateProject] = useState(false)
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)
  const [openDownloadManyDialog, setOpenDownloadManyDialog] = useState(false)
  const [openEditDataset, setOpenEditDataset] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [openImportDialog, setOpenImportDialog] = useState(false)
  const [openUploadManyDialog, setOpenUploadManyDialog] = useState(false)
  const [editableDataset, setEditableDataset] = useState<any>(undefined)
  const [editingCurrentProject, setEditingCurrentProject] = useState(false)
  const [createSubProject, setCreateSubProject] = useState(false)
  const [openProjectTreeDialog, setOpenProjectTreeDialog] = useState(false)

  // Migrated from recompose lifecycle (in ConnectedProjectContent).
  // componentDidMount portion that runs once: customerUsers, datasetReaders, datasetWriters
  useEffect(() => {
    if (routeProjectId) {
      dispatch(loadCustomerUsers(routeProjectId))
    }
    dispatch(loadDatasetReaders())
    dispatch(loadDatasetWriters())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentDidMount + componentDidUpdate: loadProjectDetails on mount and on projectId change
  useEffect(() => {
    if (routeProjectId) {
      dispatch(loadProjectDetails(routeProjectId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeProjectId])

  // Migrated from recompose withHandlers (in ConnectedProjectContent)
  const onSearchProjection = useCallback(
    id => {
      isNumeric(id)
        ? dispatch(getCoordinateSystemsById(id))
        : dispatch(filterCoordinateSystems(id))
    },
    [dispatch]
  )

  const onError = useCallback(
    (name, error) => {
      dispatch(addError(name, error))
    },
    [dispatch]
  )

  const onRefreshClick = useCallback(() => {
    dispatch(loadProjectDetails(routeProjectId, false))
    dispatch(
      loadProjectContent(
        routeProjectId,
        page,
        rowsPerPage,
        undefined,
        undefined,
        namePrefix
      )
    )
  }, [dispatch, routeProjectId, page, rowsPerPage, namePrefix])

  const onEdit = useCallback(
    item => {
      if (item) {
        if (isDataset(item)) {
          setEditableDataset(item)
          setOpenEditDataset(true)
        } else {
          setEditableDataset(item)
          setCreateSubProject(false)
          setEditingCurrentProject(true)
          setOpenCreateProject(true)
          dispatch(loadProjectMembers(item.id))
        }
      } else {
        const dataset = find((project as any)?.datasets, {
          id: first(selectedItems)?.id
        })
        setEditableDataset(dataset)
        setOpenEditDataset(true)
      }
    },
    [dispatch, project, selectedItems]
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onInviteUser = useCallback(_item => {}, [])

  const onUpdateDataset = useCallback(
    data => {
      dispatch(updateProjectDataset(project.id, data))
      setOpenEditDataset(false)
    },
    [dispatch, project]
  )

  const onDelete = useCallback(
    itemsToBeDeleted => {
      const ids = map(itemsToBeDeleted, 'id')
      setSelectedItems(prev =>
        prev.filter(item => {
          return !ids.includes(item.id)
        })
      )
      const datasetIds = map(
        itemsToBeDeleted.filter(item => 'datasetType' in item),
        'id'
      )
      const subprojectIds = map(
        itemsToBeDeleted.filter(item => !('datasetType' in item)),
        'id'
      )
      if (datasetIds.length > 0) {
        dispatch(deleteProjectDatasets(project.id, datasetIds))
      }

      if (subprojectIds.length > 0) {
        dispatch(deleteSubProjects(project.id, subprojectIds))
      }
      setOpenDeleteConfirmation(false)
    },
    [dispatch, project]
  )

  const onUploadMany = useCallback(
    filesToBeUploaded => {
      if (filesToBeUploaded.length > 0) {
        dispatch(uploadAndImportManyFiles(project.id, filesToBeUploaded))
      }
      setOpenUploadManyDialog(false)
    },
    [dispatch, project]
  )

  const onDownloadMany = useCallback(
    datasetsToBeDownloaded => {
      if (datasetsToBeDownloaded.length > 0) {
        dispatch(
          exportAndDownloadManyDatasets(datasetsToBeDownloaded, {
            reader: asIfReaderWriter.reader,
            writer: asIfReaderWriter.writer
          })
        )
      }
      setOpenDownloadManyDialog(false)
    },
    [dispatch]
  )

  const onSelectionChange = useCallback(selection => {
    setSelectedItems(selection)
  }, [])

  const onSearchTextChange = useCallback(
    newSearchText => {
      setSearchText(newSearchText)
      dispatch(setProjectPagination(0))
      dispatch(
        loadProjectContent(
          project?.id,
          0,
          rowsPerPage,
          undefined,
          undefined,
          newSearchText
        )
      )
    },
    [dispatch, project, rowsPerPage]
  )

  const onChangePage = useCallback(
    newPage => {
      dispatch(
        loadProjectContent(
          project?.id,
          newPage,
          rowsPerPage,
          undefined,
          undefined,
          namePrefix
        )
      )
      dispatch(setProjectPagination(newPage))
    },
    [dispatch, project, rowsPerPage, namePrefix]
  )

  const onChangeRowsPerPage = useCallback(
    newRowsPerPage => {
      dispatch(setProjectRowsPerPage(newRowsPerPage))
      dispatch(
        loadProjectContent(
          project?.id,
          page,
          newRowsPerPage,
          undefined,
          undefined,
          namePrefix
        )
      )
    },
    [dispatch, project, page, namePrefix]
  )

  const onImportDialogDone = useCallback(
    (data, mode, datasetId) => {
      switch (mode) {
        case UPLOAD_TYPE:
          dispatch(uploadAndImportFile(project.id, data))
          break
        case CONVERT_TYPE:
          dispatch(
            convertDataset(project.id, datasetId, data, data.importData.name)
          )
          break
        case DOWNLOAD_TYPE:
          dispatch(
            exportAndDownloadDataset(
              data.importData.name,
              datasetId,
              project.id,
              data
            )
          )
          break
        default:
          break
      }

      setOpenImportDialog(false)
    },
    [dispatch, project]
  )

  // Parent project is to be edited
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onEditCurrentProject = useCallback(() => {
    setCreateSubProject(false)
    setEditingCurrentProject(true)
    setOpenCreateProject(true)
  }, [])

  const onCreateProjectOpenClick = useCallback(() => {
    setCreateSubProject(true)
    setEditingCurrentProject(false)
    setOpenCreateProject(true)
  }, [])

  const onCreateProject = useCallback(
    data => {
      if (editingCurrentProject) {
        const isEditingSubfolder =
          editableDataset && editableDataset.id !== project.id

        if (isEditingSubfolder) {
          dispatch(
            updateProjectDetails({
              ...data,
              parentProjectIdToReload: project.id
            })
          )
          setTimeout(() => {
            dispatch(
              loadProjectContent(
                project.id,
                page,
                rowsPerPage,
                sortBy,
                sortOrder,
                namePrefix
              )
            )
          }, 500)
        } else {
          dispatch(updateProjectDetails(data))
        }
      } else {
        data.members = data.members.filter(member => {
          return member.userId !== user.id
        })
        data.parentProjectId = project.id
        dispatch(
          createNewFolder(data, () => {
            dispatch(
              loadProjectContent(
                project.id,
                page,
                rowsPerPage,
                sortBy,
                sortOrder,
                namePrefix
              )
            )
          })
        )
      }

      setOpenCreateProject(false)
    },
    [
      dispatch,
      editingCurrentProject,
      editableDataset,
      project,
      page,
      rowsPerPage,
      sortBy,
      sortOrder,
      namePrefix,
      user
    ]
  )

  const onCreateProjectCloseClick = useCallback(
    () => setOpenCreateProject(false),
    []
  )

  const onMoveToClick = useCallback(() => {
    dispatch(loadProjectRoot(project))
    setOpenProjectTreeDialog(true)
  }, [dispatch, project])

  const onMoveTo = useCallback(
    parentProjectId => {
      setSelectedItems([])

      const subprojectIds = map(
        selectedItems.filter(item => !('datasetType' in item)),
        'id'
      )

      const datasetIds = map(
        selectedItems.filter(item => 'datasetType' in item),
        'id'
      )

      if (subprojectIds.length > 0 || datasetIds.length > 0) {
        dispatch(
          moveContent(parentProjectId, subprojectIds, datasetIds, project.id)
        )
      }

      dispatch(resetProjectInitialTree)
      setOpenProjectTreeDialog(false)
    },
    [dispatch, selectedItems, project]
  )

  const onExpand = useCallback(
    nodeId => {
      if (!(nodeId && nodeId !== 'project')) return
      dispatch(loadProjectSubprojects(nodeId, false))
    },
    [dispatch]
  )

  const onCloseTreeDialog = useCallback(() => {
    dispatch(resetProjectInitialTree)
    setOpenProjectTreeDialog(false)
  }, [dispatch])

  const onHandleRequestSort = useCallback(
    (newOrderBy, newOrder) => {
      dispatch(setProjectPagination(0))
      dispatch(
        loadProjectContent(
          project?.id,
          0,
          rowsPerPage,
          newOrderBy,
          newOrder,
          namePrefix
        )
      )
    },
    [dispatch, project, rowsPerPage, namePrefix]
  )

  const selectionSize = size(selectedItems)
  const selectedFolders = selectedItems.filter(item => !isDataset(item))
  const selectedDatasets = xor(selectedItems, selectedFolders)
  const selectionOfDatasetsSize = size(selectedDatasets)

  const {
    canGrantAccess,
    canCreateContent,
    canDeleteContent,
    canReadContent,
    canUpdateContent
  } = get(project, 'capabilities', {})

  // FIXME: use the intl message format for plurals (https://github.com/yahoo/react-intl/wiki/API#message-syntax)
  const messageSingleDataset = intl.formatMessage({
    id: 'projectContent.confirmationDialog.message.singleItem'
  })
  const messageMultipleDatasets = intl.formatMessage({
    id: 'projectContent.confirmationDialog.message.multipleItems'
  })

  const canDownload = canReadContent && selectionOfDatasetsSize >= 1

  // For deleting, either the parent projects needs to can delete content or
  // In case only folders are selected, they can also be deleted when each folder allows it
  const canDelete =
    (selectionSize > 0 && canDeleteContent) ||
    (selectionSize > 0 &&
      selectionOfDatasetsSize === 0 &&
      every(selectedFolders, 'capabilities.canDelete'))
  // For moving same access rights are needed than for deleting
  const canMove = canDelete

  const members = enhanceProjectMembers(get(project, 'members'), customerUsers)

  const projectMembers = useTypedSelector(
    state => state['project'].members.data
  )
  const [manageAccessPanel, setManageAccessPanel] = useState<{
    open: boolean
    projectId: string | null
  }>({ open: false, projectId: null })
  const editableProject =
    editableDataset && !isDataset(editableDataset)
      ? {
          ...editableDataset,
          members: enhanceProjectMembers(projectMembers, customerUsers)
        }
      : {
          ...project,
          members
        }

  const subprojectIds = map(selectedFolders, 'id')

  const disabledMovingTargetIds = () => {
    let cannotCreateContentIds = []

    projectTree &&
      Object.values(projectTree).forEach((subprojects: any) => {
        const ids = map(
          subprojects.filter(
            subproject =>
              !subproject.capabilities ||
              !subproject.capabilities.canCreateContent
          ),
          'id'
        )
        cannotCreateContentIds = cannotCreateContentIds.concat(ids)
      })
    return cannotCreateContentIds
  }
  const [
    selectedDatasetsNotInDedicatedStorage,
    setSelectedDatasetsNotInDedicatedStorage
  ] = useState([])
  const [conversionName, setConversionName] = useState('')
  const [conversionDatasetId, setConversionDatasetId] = useState<any>()

  const [conversionDialogMode, setConversionDialogMode] = useState(UPLOAD_TYPE)
  const [initialConversionStep, setInitialConversionStep] = useState(1)
  const [fileToUpload, setFileToUpload] = useState<File | null>(null)
  const [manyFilesToUpload, setManyFilesToUpload] = useState<any>(null)
  const [conversionDialogIsOpen, setConversionDialogIsOpen] = useState(false)
  const [suitableReaders, setSuitableReaders] = useState<
    IDatasetReader[] | null
  >(null)
  const [itemsToBeDeleted, setItemsToBeDeleted] = useState<any>(null)
  const [disableConvert, setDisableConvert] = useState(false)
  const [moveDlg, setMoveDlg] = useState<{ open: boolean; datasetId?: string }>(
    {
      open: false
    }
  )
  const [copyDlg, setCopyDlg] = useState<{ open: boolean; datasetId?: string }>(
    {
      open: false
    }
  )

  const onMoveFromRow = useCallback((row: { id: string }) => {
    setMoveDlg({ open: true, datasetId: row.id })
  }, [])

  const onCopyFromRow = useCallback((row: { id: string }) => {
    setCopyDlg({ open: true, datasetId: row.id })
  }, [])

  const onMovedRefresh = useCallback(() => {
    onRefreshClick()
    setMoveDlg({ open: false })
  }, [onRefreshClick])

  const onCopiedRefresh = useCallback(() => {
    onRefreshClick()
    setCopyDlg({ open: false })
  }, [onRefreshClick])

  const openUploadDialog = useCallback(
    open => {
      setConversionDialogMode(UPLOAD_TYPE)
      setSuitableReaders(datasetFileReaders)
      setConversionDialogIsOpen(open)
    },
    [datasetFileReaders]
  )

  const onManageAccessFromRow = useCallback(
    item => {
      if (!isDataset(item) && canGrantAccess) {
        setManageAccessPanel({ open: true, projectId: item.id })
      }
    },
    [canGrantAccess]
  )

  const onManageAccessForCurrentFolder = useCallback(() => {
    if (project && canGrantAccess) {
      setManageAccessPanel({ open: true, projectId: project.id })
    }
  }, [project, canGrantAccess])

  const processFiles = useCallback(
    files => {
      const selectedFileCount = files ? files.length : 0
      setDisableConvert(false)
      if (selectedFileCount === 1) {
        setFileToUpload(files[0])
        setConversionName(files[0].name)
        openUploadDialog(true)
      } else if (selectedFileCount > 1) {
        const selectedFiles: File[] = []
        for (let i = 0; i < selectedFileCount; i++) {
          selectedFiles.push(files[i])
        }
        setManyFilesToUpload(selectedFiles)
        setOpenUploadManyDialog(true)
      }
    },
    [
      openUploadDialog,
      setDisableConvert,
      setFileToUpload,
      setConversionName,
      setManyFilesToUpload,
      setOpenUploadManyDialog
    ]
  )

  const onFileDropped = useCallback(
    files => {
      processFiles(files)
    },
    [processFiles]
  )

  const onFileSelect = useCallback(
    event => {
      event.target && event.target.files && processFiles(event.target.files)
      event.target.value = null
    },
    [processFiles]
  )

  const handleCloseConfirmConvertManyDialog = useCallback(() => {
    setManyFilesToUpload(null)
    setOpenUploadManyDialog(false)
  }, [setOpenUploadManyDialog, setManyFilesToUpload])

  const handleUploadManyFiles = useCallback(() => {
    onUploadMany(manyFilesToUpload)
    setOpenUploadManyDialog(false)
  }, [onUploadMany, manyFilesToUpload, setOpenUploadManyDialog])

  const handleCloseConfirmDownloadManyDialog = useCallback(() => {
    setManyFilesToUpload(null)
    setOpenDownloadManyDialog(false)
  }, [setOpenDownloadManyDialog, setManyFilesToUpload])

  const handleDownloadManyFiles = useCallback(() => {
    onDownloadMany(selectedDatasetsNotInDedicatedStorage)
    setOpenDownloadManyDialog(false)
    onSelectionChange([])
  }, [
    onDownloadMany,
    selectedDatasetsNotInDedicatedStorage,
    setOpenDownloadManyDialog,
    onSelectionChange
  ])

  const handleCloseConversionDialog = useCallback(() => {
    setDisableConvert(false)
    setInitialConversionStep(1)
    setConversionDialogIsOpen(false)
    setFileToUpload(null)
    setConversionName('')
    setConversionDatasetId(null)
  }, [
    setInitialConversionStep,
    setConversionDialogIsOpen,
    setFileToUpload,
    setConversionName,
    setConversionDatasetId
  ])

  const onUpload = useCallback(
    (mode, conversionValues, datasetId, fileName) => {
      const name =
        mode === UPLOAD_TYPE
          ? fileToUpload!.name
          : fileName
          ? fileName
          : 'Export'
      const data = conversionValues
        ? { ...conversionValues }
        : {
            name: name,
            reader: asIfReaderWriter.reader,
            writer: asIfReaderWriter.writer
          }
      const payload =
        mode === UPLOAD_TYPE
          ? {
              file: fileToUpload,
              importData: data
            }
          : {
              importData: data
            }
      onImportDialogDone(payload, mode, datasetId)
      setConversionDialogIsOpen(false)
      setFileToUpload(null)
      setConversionName('')
      setConversionDatasetId(null)
      onSelectionChange([])
    },
    [fileToUpload, onImportDialogDone, onSelectionChange]
  )

  useEffect(() => {
    setSuitableReaders(datasetReaders)
  }, [datasetReaders])

  const onDeleteOneItem = useCallback(
    item => {
      setItemsToBeDeleted([item])
      setOpenDeleteConfirmation(true)
    },
    [setOpenDeleteConfirmation, setItemsToBeDeleted]
  )
  const onDeleteItems = useCallback(() => {
    setItemsToBeDeleted(selectedItems)
    setOpenDeleteConfirmation(true)
  }, [setOpenDeleteConfirmation, setItemsToBeDeleted, selectedItems])

  const onOkDeleteItems = useCallback(() => {
    onDelete(itemsToBeDeleted)
  }, [onDelete, itemsToBeDeleted])

  const onEditOneItem = useCallback(
    item => {
      onEdit(item)
    },
    [onEdit]
  )

  const onConvertDataset = useCallback(
    (item, name) => {
      if (DATASETFORMAT in item) {
        setConversionName(item.name)
        setConversionDatasetId(item.id)
        const readersForDatasetFormat = datasetReaders.filter(reader => {
          return toLower(reader.datasetFormat) === toLower(item.datasetFormat)
        })
        setSuitableReaders(readersForDatasetFormat)
        setConversionDialogMode(
          name === CONVERT_TYPE ? CONVERT_TYPE : DOWNLOAD_TYPE
        )
        const writers =
          readersForDatasetFormat &&
          readersForDatasetFormat.length > 0 &&
          readersForDatasetFormat[0].writers

        if (!writers) {
          // There might be dataset formats without writer support
          onError(
            'noWriter',
            intl.formatMessage(
              name === CONVERT_TYPE
                ? localMessages.noWriterToConvert
                : localMessages.noWriterToDownload
            )
          )
        } else {
          // For converts of existing datasets and
          // downloads without FileWriter available
          // we want to hide save as-if option
          setInitialConversionStep(
            name === CONVERT_TYPE
              ? 2
              : writers && writers.includes(FILEWRITER)
              ? 1
              : 2
          )
          // For downloads with only FileWriter available
          // we want to disable conversion option
          setDisableConvert(
            name === CONVERT_TYPE
              ? false
              : writers && writers.length === 1 && writers.includes(FILEWRITER)
          )
          setConversionDialogIsOpen(true)
        }
      }
    },
    [datasetReaders, onError, intl]
  )

  const onCancelDeleteItems = useCallback(() => {
    setItemsToBeDeleted(null)
    setOpenDeleteConfirmation(false)
  }, [setOpenDeleteConfirmation, setItemsToBeDeleted])

  const onDownloadClick = useCallback(() => {
    if (!canDownload) return

    if (selectedDatasets.length === 1) {
      onConvertDataset(selectedDatasets[0], DOWNLOAD_TYPE)
    } else {
      const dedicatedStorage =
        dedicatedWriters &&
        dedicatedWriters.map(dedicatedWriter => dedicatedWriter.datasetFormat)
      if (dedicatedStorage && dedicatedStorage.length) {
        const filterDatasets = selectedDatasets
          ? selectedDatasets.filter(
              selectedDataset =>
                !dedicatedStorage.includes(selectedDataset.datasetFormat)
            )
          : []
        setSelectedDatasetsNotInDedicatedStorage(filterDatasets)
        setOpenDownloadManyDialog(true)
      }
    }
  }, [
    onConvertDataset,
    selectedDatasets,
    canDownload,
    dedicatedWriters,
    setOpenDownloadManyDialog
  ])

  return (
    <>
      <AppNav tenantId={tenantId} />
      <ProjectsTreeDialog
        open={openProjectTreeDialog}
        onClose={onCloseTreeDialog}
        onExpand={onExpand}
        onMove={onMoveTo}
        parentId={project && project.id}
        projectTree={projectTree}
        selectedIds={subprojectIds}
        disabledIds={disabledMovingTargetIds()}
        loadingInitialTree={loadingInitialTree}
      />
      <ConfirmationDialog
        open={openDeleteConfirmation}
        title={intl.formatMessage(localMessages.confirmationDeleteTitle)}
        message={
          selectionSize > 1 ? messageMultipleDatasets : messageSingleDataset
        }
        ok={intl.formatMessage(messages.yesDelete)}
        onOk={onOkDeleteItems}
        onCancel={onCancelDeleteItems}
      />
      <DatasetUploadManyDialog
        onOk={handleUploadManyFiles}
        onCancel={handleCloseConfirmConvertManyDialog}
        open={openUploadManyDialog}
        includesZippedFiles={
          manyFilesToUpload &&
          manyFilesToUpload.filter(file => file.name.endsWith('.zip')).length >
            0
        }
        selectedFilesCount={manyFilesToUpload ? manyFilesToUpload.length : 0}
      />
      <DatasetDownloadManyDialog
        onOk={handleDownloadManyFiles}
        onCancel={handleCloseConfirmDownloadManyDialog}
        open={openDownloadManyDialog}
        selectedFilesCount={
          selectedDatasetsNotInDedicatedStorage
            ? selectedDatasetsNotInDedicatedStorage.length
            : 0
        }
      />

      <DatasetConversionDialog
        datasetId={conversionDatasetId}
        datasetReaders={suitableReaders ?? []}
        datasetWriters={datasetWriters}
        dedicatedWriters={
          dedicatedWriters && dedicatedWriters.length > 0
            ? dedicatedWriters.map(dedicated => dedicated.name)
            : []
        }
        disableConvert={disableConvert}
        loadingDatasetReaders={
          showDatasetReadersLoader || showDatasetWritersLoader
        }
        fileName={conversionName}
        mode={conversionDialogMode as dialogMode}
        open={conversionDialogIsOpen}
        handleUpload={onUpload}
        initialStep={initialConversionStep}
        handleClose={handleCloseConversionDialog}
        projections={selectedCoordinateSystems}
        projectionsLoading={loadingCoordinateSystems}
        onProjectionSearchTextChanged={onSearchProjection}
        searchCoordinateSystemsById={searchCoordinateSystemsById}
      />
      <StyledTableTopHeader>
        <StyledProjectDetails>
          <AppBreadcrumbNav />
        </StyledProjectDetails>
        <StyledTableTopActionButtons>
          <StyledLeftActionButtons>
            <SearchInput
              id="project-details-search-input"
              placeholder={intl.formatMessage(localMessages.searchPlaceholder)}
              autoFocus
              text={searchText}
              onTextChange={onSearchTextChange}
            />

            <RefreshButton onClick={onRefreshClick} />

            {selectionSize > 0 && (
              <DownloadButton
                disabled={!canDownload}
                onClick={onDownloadClick}
              />
            )}
            {selectionSize > 0 && (
              <DeleteButton disabled={!canDelete} onClick={onDeleteItems} />
            )}
            {selectionSize > 0 && (
              <MoveToButton disabled={!canMove} onClick={onMoveToClick} />
            )}
          </StyledLeftActionButtons>
          <StyledRightActionButtons>
            <FileChooserButton
              disabled={!canCreateContent}
              onFileSelect={onFileSelect}
            />

            <StyledCreateSubprojectButton>
              <CreateSubprojectButton
                disabled={!canCreateContent}
                onClick={onCreateProjectOpenClick}
              />
            </StyledCreateSubprojectButton>

            <StyledFolderAccessButton>
              <FolderAccessButton
                disabled={!canGrantAccess}
                onClick={onManageAccessForCurrentFolder}
              />
            </StyledFolderAccessButton>
          </StyledRightActionButtons>
        </StyledTableTopActionButtons>

        {detailsFailed && (
          <StyledDetailsError role="alert">
            <Alert width="20" height="20" viewBox="0 0 40 40" />
            <Typography variant="body2" color="inherit">
              {intl.formatMessage(localMessages.detailsFailed)}
            </Typography>
            <Button size="small" color="inherit" onClick={onRetryDetails}>
              {intl.formatMessage(localMessages.detailsFailedRetry)}
            </Button>
          </StyledDetailsError>
        )}
      </StyledTableTopHeader>
      <ConnectedProjectContentTable
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        searchText={searchText}
        selectedItems={selectedItems}
        onSelectionChange={onSelectionChange}
        onDelete={canUpdateContent ? onDeleteOneItem : () => {}}
        onEdit={canUpdateContent ? onEditOneItem : () => {}}
        onManageAccess={onManageAccessFromRow}
        onDownload={onConvertDataset}
        onConvert={canUpdateContent ? onConvertDataset : () => {}}
        topOffset={0}
        parentCapabilities={{
          canDeleteContent: canDeleteContent,
          canUpdateContent: canUpdateContent,
          canCreateContent: canCreateContent,
          canReadContent: canReadContent
        }}
        processDroppedFiles={canCreateContent ? onFileDropped : () => {}}
        onHandleRequestSort={onHandleRequestSort}
        onMove={onMoveFromRow}
        onCopy={onCopyFromRow}
      />
      <MoveDatasetDialog
        open={moveDlg.open}
        datasetId={moveDlg.datasetId || ''}
        onClose={() => setMoveDlg({ open: false })}
        onMoved={onMovedRefresh}
      />
      <CopyDatasetDialog
        open={copyDlg.open}
        datasetId={copyDlg.datasetId || ''}
        onClose={() => setCopyDlg({ open: false })}
        onCopied={onCopiedRefresh}
      />
      <MikeSlidingPanel
        position="right"
        isOpen={openEditDataset}
        onClose={() => setOpenEditDataset(false)}
        titleArea={
          editableDataset && editableDataset.datasetType === 'file'
            ? intl.formatMessage(localMessages.editFileTitle)
            : intl.formatMessage(localMessages.editDatasetTitle)
        }
        contentArea={
          <EditDatasetForm
            initialValues={editableDataset}
            onSubmit={onUpdateDataset}
          />
        }
        actionsArea={null}
        noGrayOverlay={false}
      />
      <MikeSlidingPanel
        position="right"
        isOpen={openCreateProject}
        onClose={onCreateProjectCloseClick}
        titleArea={
          editingCurrentProject
            ? intl.formatMessage(localMessages.editProjectTitle)
            : intl.formatMessage(localMessages.createFolderTitle)
        }
        contentArea={
          <CreateProjectForm
            editing={editingCurrentProject}
            initialValues={editingCurrentProject ? editableProject : null}
            onSubmit={onCreateProject}
            isSubProject={
              createSubProject || 'parentProjectId' in editableProject
            }
            totalCount={totalCount}
            hideMemberField={editingCurrentProject}
          />
        }
        actionsArea={null}
        noGrayOverlay={false}
      />
      <FolderAccessDialog
        open={manageAccessPanel.open}
        projectId={manageAccessPanel.projectId}
        onClose={() => setManageAccessPanel({ open: false, projectId: null })}
      />
    </>
  )
}
ProjectContent.propTypes = {
  customerUsers: PropTypes.objectOf(AppPropTypes.customerUser).isRequired,
  loadingInitialTree: PropTypes.bool.isRequired,
  project: AppPropTypes.project,
  projectTree: PropTypes.object,
  user: AppPropTypes.user,
  datasetReaders: PropTypes.arrayOf(AppPropTypes.datasetReader).isRequired,
  datasetFileReaders: PropTypes.arrayOf(AppPropTypes.datasetReader),
  showDatasetReadersLoader: PropTypes.bool.isRequired,
  datasetWriters: PropTypes.arrayOf(AppPropTypes.datasetWriter),
  dedicatedWriters: PropTypes.arrayOf(AppPropTypes.datasetWriter),
  showDatasetWritersLoader: PropTypes.bool.isRequired,
  loadingApps: PropTypes.bool,
  apps: PropTypes.object,
  loadingCoordinateSystems: PropTypes.bool,
  selectedCoordinateSystems: PropTypes.array,
  searchCoordinateSystemsById: PropTypes.bool,
  totalCount: PropTypes.number,
  features: PropTypes.array,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  namePrefix: PropTypes.string,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.string
}

export default ProjectContent
